import type {
  DataEditorRef,
  GridCell,
  GridKeyEventArgs,
  Item,
} from "@glideapps/glide-data-grid";
import type { RefObject } from "react";
import type { IAiSpreadsheetRegion } from "@/payroll/ai-script/types";
import { getAiSpreadsheetCellDisplayValue } from "./cell-display-value";
import {
  getSpreadsheetRegionColumnValues,
  resolveSpreadsheetRegionColumnType,
} from "./spreadsheet-region-view";
import {
  getTabNavigationSelection,
  type TabNavigationTrigger,
} from "./tab-navigation";
import type { ViewerActiveSheet } from "./use-viewer-active-sheet";
import type { ViewerUiState } from "./use-viewer-ui-state";
import type { useWorkbookClipboard } from "./use-workbook-clipboard";
import { findRegionHeaderForRecord } from "./viewer-helpers";

type WorkbookClipboard = ReturnType<typeof useWorkbookClipboard>;

interface CreateGridKeyboardHandlersParams {
  columnCount: number;
  gridRef: RefObject<DataEditorRef | null>;
  clipboard: Pick<
    WorkbookClipboard,
    "copiedSelection" | "clearCopiedSelection"
  >;
  sheet: Pick<
    ViewerActiveSheet,
    "adaptedSheet" | "activeRegions" | "activeLoadedSheet"
  >;
  ui: Pick<
    ViewerUiState,
    | "gridSelection"
    | "setGridSelection"
    | "setRowMenu"
    | "setColumnHeaderMenu"
    | "setRegionPreview"
    | "setSpreadsheetColumnMenu"
  >;
}

export const createGridKeyboardHandlers = ({
  columnCount,
  gridRef,
  clipboard: { copiedSelection, clearCopiedSelection },
  sheet: { adaptedSheet, activeRegions, activeLoadedSheet },
  ui: {
    gridSelection,
    setGridSelection,
    setRowMenu,
    setColumnHeaderMenu,
    setRegionPreview,
    setSpreadsheetColumnMenu,
  },
}: CreateGridKeyboardHandlersParams) => {
  const findRegionHeaderAtCell = (
    cell: Item,
  ): IAiSpreadsheetRegion | undefined => {
    const record = adaptedSheet?.getCellRecord(cell[0], cell[1]);
    if (!record) return undefined;

    return findRegionHeaderForRecord(activeRegions, record);
  };

  const openSpreadsheetColumnMenu = (
    cell: Item,
    position: { x: number; y: number },
    focusOnOpen = false,
  ): boolean => {
    const region = findRegionHeaderAtCell(cell);
    const record = adaptedSheet?.getCellRecord(cell[0], cell[1]);
    if (!region || !record || !activeLoadedSheet) return false;

    const setting = region.column_settings[String(record.col)];
    if (!setting?.filterable && !setting?.sortable) return false;

    setRowMenu(null);
    setColumnHeaderMenu(null);
    setRegionPreview(null);
    setSpreadsheetColumnMenu({
      ...position,
      column: record.col,
      region,
      values: getSpreadsheetRegionColumnValues(
        activeLoadedSheet.cells,
        region,
        record.col,
      ),
      columnType: resolveSpreadsheetRegionColumnType(
        activeLoadedSheet.cells,
        region,
        record.col,
      ),
      label: getAiSpreadsheetCellDisplayValue(record).trim(),
      focusOnOpen,
    });
    return true;
  };

  const navigateWithTab = (trigger: TabNavigationTrigger) => {
    const nextSelection = getTabNavigationSelection({
      columnCount,
      rowCount: adaptedSheet?.rowCount ?? 0,
      selection: gridSelection,
      trigger,
    });
    if (!nextSelection?.current) return false;

    setGridSelection(nextSelection);
    const target = nextSelection.current.cell;
    gridRef.current?.scrollTo(target[0], target[1], "both");

    return true;
  };

  const handleGridKeyDown = (event: GridKeyEventArgs) => {
    if (event.key === "Escape" && copiedSelection) {
      clearCopiedSelection();
      return;
    }

    if (
      event.key === "ArrowDown" &&
      event.altKey &&
      gridSelection.current?.cell &&
      event.bounds &&
      openSpreadsheetColumnMenu(
        gridSelection.current.cell,
        {
          x: event.bounds.x,
          y: event.bounds.y + event.bounds.height,
        },
        true,
      )
    ) {
      event.cancel();
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const moved = navigateWithTab({
      mode: "selection",
      key: event.key,
      altKey: event.altKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
    });
    if (!moved) return;

    // Cancel Glide's Tab keybinding so this handler owns selection moves.
    event.cancel();
    event.preventDefault();
    event.stopPropagation();
  };

  const handleFinishedEditing = (
    _newValue: GridCell | undefined,
    movement: Item,
  ) => {
    navigateWithTab({ mode: "editing", movement });
  };

  return {
    openSpreadsheetColumnMenu,
    handleGridKeyDown,
    handleFinishedEditing,
  };
};
